import { local } from '@/lib/storage';
import {
  mockCanvases,
  mockInitialBookmarks,
  mockInitialCanvasVideos,
  mockPlans,
  mockUser,
} from '@/mocks/data';
import type { ApiUser, CanvasSummary, UserSubscription } from '@/api/types';

const STATE_KEY = 'shotbook_mock_state';
const STATE_VERSION = 6;

interface MockState {
  version: number;
  pendingEmails: string[];
  verified: boolean;
  /** Производное: подписка есть ИЛИ бесплатный режим. Меняется только через setSubscription/setFreeMode. */
  fullAccess: boolean;
  /** Глобальный бесплатный режим (BILL-1): доступ открыт всем, платный слой выключен. */
  freeMode: boolean;
  subscription: UserSubscription | null;
  onboardingCompletedAt: string | null;
  welcomeShownAt: string | null;
  /** Дневная квота детальных просмотров бесплатного тарифа: день и id открытых видео. */
  detailViews: { day: string; ids: number[] };
  bookmarks: number[];
  /** id видео → своё название закладки (PATCH /bookmarks/:id). Нет поля — названий нет. */
  bookmarkTitles?: Record<string, string>;
  canvases: CanvasSummary[];
  /** id канваса → id видео каталога на нём (CNV-21 / saved_canvas_ids). */
  canvasVideos: Record<string, number[]>;
  /** Сохранённые документы полотна (PUT /canvases/:id/document). */
  canvasDocs: Record<string, { nodes: unknown[]; connections: unknown[]; version: number }>;
  nextCanvasId: number;
  nextSearchEventId: number;
}

function defaults(): MockState {
  return {
    version: STATE_VERSION,
    pendingEmails: [],
    verified: true,
    fullAccess: false,
    // Тумблер для проверок: localStorage shotbook_mock_free_mode=1 включает бесплатный режим.
    freeMode: local.get('shotbook_mock_free_mode') === '1',
    subscription: null,
    // Существующий пользователь получил флаг бэкфиллом (ONB-2); регистрация сбрасывает в null.
    onboardingCompletedAt: '2026-09-01T10:00:00Z',
    welcomeShownAt: null,
    detailViews: { day: '', ids: [] },
    bookmarks: [...new Set(mockInitialBookmarks)],
    canvases: mockCanvases.map((c) => ({ ...c })),
    canvasVideos: Object.fromEntries(
      Object.entries(mockInitialCanvasVideos).map(([k, v]) => [k, [...v]]),
    ),
    canvasDocs: {},
    nextCanvasId: 100,
    nextSearchEventId: 1000,
  };
}

const stored = local.getJson<MockState>(STATE_KEY);
let state: MockState = stored && stored.version === STATE_VERSION ? stored : defaults();

export function getState(): MockState {
  return state;
}

export function patchState(patch: Partial<MockState>): MockState {
  state = { ...state, ...patch };
  local.setJson(STATE_KEY, state);
  return state;
}

export function resetState(): void {
  state = defaults();
  local.remove(STATE_KEY);
}

export const MOCK_TOKEN = 'mock-access-token';

/** Канвасы, на которых лежит видео. */
export function canvasIdsForVideo(videoId: number): number[] {
  return Object.entries(getState().canvasVideos)
    .filter(([, ids]) => ids.includes(videoId))
    .map(([canvasId]) => Number(canvasId));
}

export const FREE_CANVASES = 1;
export const FREE_DETAIL_VIEWS = 7;

const today = () => new Date().toISOString().slice(0, 10);

/** Остаток дневной квоты; смена даты обнуляет счётчик. */
export function detailViewsQuota() {
  const s = getState();
  const views =
    s.detailViews && s.detailViews.day === today() ? s.detailViews : { day: today(), ids: [] };
  const used = Math.min(views.ids.length, FREE_DETAIL_VIEWS);
  const resets = new Date();
  resets.setUTCHours(24, 0, 0, 0);
  return {
    limit: FREE_DETAIL_VIEWS,
    used,
    remaining: FREE_DETAIL_VIEWS - used,
    resets_at: resets.toISOString(),
  };
}

/** Списать просмотр: то же видео в тот же день лимит не тратит. false — квота исчерпана. */
export function registerDetailView(videoId: number): boolean {
  const s = getState();
  const views =
    s.detailViews && s.detailViews.day === today()
      ? { ...s.detailViews, ids: [...s.detailViews.ids] }
      : { day: today(), ids: [] as number[] };
  if (views.ids.includes(videoId)) return true;
  if (views.ids.length >= FREE_DETAIL_VIEWS) return false;
  views.ids.push(videoId);
  patchState({ detailViews: views });
  return true;
}

const DAY = 86_400_000;

/**
 * Две строки подписки для /me, как у бэкенда (AuthController::me): строка доступа
 * (`subscription`) и живая платная строка Paddle (`paddle_subscription`).
 * Тумблеры для проверок состояний — только в /me, кап каталога они не трогают:
 * localStorage `shotbook_mock_paddle_status` = past_due (списание не прошло, доступ
 * ещё есть) · past_due_expired (дунинг, период истёк) · paused (пауза);
 * `shotbook_mock_grant=1` — доступ даёт грант администратора с более поздним сроком,
 * платная строка замаскирована (карточка «Paid subscription»).
 */
export function subscriptionView(): {
  access: UserSubscription | null;
  paid: UserSubscription | null;
} {
  const row = getState().subscription;
  const paddleStatus = local.get('shotbook_mock_paddle_status');
  const paid: UserSubscription | null = !row?.paddle_subscription_id
    ? null
    : paddleStatus === 'past_due' || paddleStatus === 'past_due_expired'
      ? { ...row, paddle_status: 'past_due' }
      : paddleStatus === 'paused'
        ? { ...row, status: 'paused', paddle_status: 'paused' }
        : row;
  const yearly = mockPlans.find((p) => p.slug === 'yearly')!;
  const grant: UserSubscription | null =
    local.get('shotbook_mock_grant') === '1'
      ? {
          plan: { id: yearly.id, name: `Pro · ${yearly.name}`, slug: yearly.slug },
          expires_at: new Date(Date.now() + 400 * DAY).toISOString(),
          paddle_subscription_id: null,
          paddle_status: null,
          status: 'active',
          cancelled_at: null,
        }
      : null;
  const paidGivesAccess = !!paid && paid.status !== 'paused' && paddleStatus !== 'past_due_expired';
  return { access: grant ?? (paidGivesAccess ? paid : null), paid };
}

export function buildApiUser(overrides: Partial<ApiUser> = {}): ApiUser {
  const s = getState();
  const { access, paid } = subscriptionView();
  const fullAccess = !!access || s.freeMode;
  return {
    id: mockUser.id,
    name: mockUser.name,
    email: mockUser.email,
    email_verified_at: s.verified ? '2026-09-01T10:00:00Z' : null,
    has_subscription: !!access,
    has_full_access: fullAccess,
    free_mode: s.freeMode,
    is_admin: false,
    is_internal: false,
    clarity: { project_id: null },
    welcome_prompt_shown_at: s.welcomeShownAt,
    onboarding_completed_at: s.onboardingCompletedAt,
    quota: fullAccess
      ? null
      : {
          detail_views: detailViewsQuota(),
          canvases: {
            limit: FREE_CANVASES,
            used: s.canvases.filter((c) => c.name !== 'Saved').length,
          },
        },
    subscription: access,
    paddle_subscription: paid,
    has_paddle_customer: !!paid,
    ...overrides,
  };
}

/** Единственная точка смены подписки: держит `fullAccess` в согласии с ней. */
export function setSubscription(subscription: UserSubscription | null): void {
  patchState({ subscription, fullAccess: !!subscription || getState().freeMode });
}

export function setFreeMode(freeMode: boolean): void {
  patchState({ freeMode, fullAccess: !!getState().subscription || freeMode });
}
